import { createEffect, createSignal, on, onCleanup, onMount, type JSX } from "solid-js";
import { dragTarget, snapCollapsed, takesOver } from "./sheetSnap";

/** Room left under the peek block when collapsed. */
const PEEK_GAP = 12;
/** Without a [data-sheet-peek] block: the handle and a line of content. */
const DEFAULT_PEEK = 96;

/**
 * Figma bottom sheet (02b, 03, 05b, 06): 24 px top radius, 40×4 handle, close icon 24 px.
 * `above`: map controls that ride on the sheet's top edge (e.g. the locate button), outside the
 * scrolling area so they are never clipped; they follow the sheet when it collapses.
 * `collapsible`: drag the sheet down (anywhere once its content is at the top) or tap the handle to
 * keep only its top, down to the bottom of its `[data-sheet-peek]` element, and see the map; drag
 * up or tap to open it again.
 */
export function Sheet(props: {
  children: JSX.Element;
  onClose?: () => void;
  /** false: no handle; "overlay": the content draws its own (P2's violet header), the collapsible
   *  sheet only lays an invisible 30 px tap zone over it. */
  handle?: boolean | "overlay";
  above?: JSX.Element;
  collapsible?: boolean;
  /** After the sheet settled, collapsed or open (e.g. to frame the map again). */
  onCollapsedChange?: (collapsed: boolean) => void;
  /** A new value opens it again (the sheet stays mounted while another vehicle is selected). */
  openKey?: unknown;
}) {
  let section!: HTMLElement;
  const [collapsed, setCollapsed] = createSignal(false);
  /** Live offset while the finger drags the handle. */
  const [drag, setDrag] = createSignal<number>();
  /** How far the sheet can go down: its height minus the peek. */
  const [max, setMax] = createSignal(0);
  const offset = () => drag() ?? (collapsed() ? max() : 0);

  const measure = () => {
    const peek = section.querySelector<HTMLElement>("[data-sheet-peek]");
    const keep = peek ? peek.offsetTop + peek.offsetHeight + PEEK_GAP : DEFAULT_PEEK;
    setMax(Math.max(0, section.offsetHeight - keep));
  };
  onMount(() => {
    if (!props.collapsible) return;
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(section);
    onCleanup(() => ro.disconnect());
  });
  createEffect(
    on(
      () => props.openKey,
      () => setCollapsed(false),
      { defer: true },
    ),
  );
  createEffect(
    on(
      collapsed,
      (c) => {
        if (c) section.scrollTo({ top: 0 }); // reopened, it starts from its top again
        const t = setTimeout(() => props.onCollapsedChange?.(c), 240); // after the transition
        onCleanup(() => clearTimeout(t));
      },
      { defer: true },
    ),
  );

  // Drags anywhere on the sheet (touch): `dragTarget` decides between moving the sheet and letting
  // the content scroll; a horizontal drag (slider) or one on [data-sheet-nodrag] is never taken.
  let touch:
    | {
        x: number;
        y: number;
        offset: number;
        target?: "sheet" | "content" | undefined;
        horizontal?: boolean;
        from?: number;
      }
    | undefined;
  let last: { y: number; t: number; v: number } | undefined;
  const onTouchStart = (e: TouchEvent) => {
    const t = e.touches[0];
    if (!t || e.touches.length > 1 || (e.target as Element).closest("[data-sheet-nodrag]")) {
      touch = undefined;
      return;
    }
    touch = { x: t.clientX, y: t.clientY, offset: offset() };
    last = { y: t.clientY, t: e.timeStamp, v: 0 };
  };
  const onTouchMove = (e: TouchEvent) => {
    const t = e.touches[0];
    if (!touch || !t || !last) return;
    const step = t.clientY - last.y;
    const dt = Math.max(1, e.timeStamp - last.t);
    last = { y: t.clientY, t: e.timeStamp, v: step / dt };
    if (!touch.target) {
      const dx = t.clientX - touch.x;
      const dy = t.clientY - touch.y;
      touch.target = dragTarget({
        dx,
        dy,
        collapsed: collapsed(),
        offset: offset(),
        scrollTop: section.scrollTop,
      });
      touch.horizontal = Math.abs(dx) > Math.abs(dy);
      if (touch.target === "sheet") touch.from = t.clientY; // no jump by the slop
    } else if (
      touch.target === "content" &&
      takesOver({ horizontal: !!touch.horizontal, step, scrollTop: section.scrollTop })
    ) {
      // The content is back at its top and the finger still pulls down: the sheet follows now.
      touch.target = "sheet";
      touch.offset = offset();
      touch.from = t.clientY;
    }
    if (touch.target !== "sheet") return;
    if (e.cancelable) e.preventDefault(); // the sheet moves, the content doesn't scroll
    setDrag(Math.min(max(), Math.max(0, touch.offset + t.clientY - (touch.from ?? touch.y))));
  };
  const onTouchEnd = () => {
    const t = touch;
    touch = undefined;
    if (t?.target !== "sheet") return;
    setCollapsed(snapCollapsed(drag() ?? t.offset, max(), last?.v ?? 0));
    setDrag(undefined);
  };
  onMount(() => {
    if (!props.collapsible) return;
    section.addEventListener("touchstart", onTouchStart, { passive: true });
    section.addEventListener("touchmove", onTouchMove, { passive: false });
    section.addEventListener("touchend", onTouchEnd);
    section.addEventListener("touchcancel", onTouchEnd);
  });

  return (
    <div
      class="absolute inset-x-0 bottom-0 z-20"
      style={
        props.collapsible
          ? {
              transform: `translateY(${offset()}px)`,
              transition: drag() === undefined ? "transform 220ms ease-out" : "none",
            }
          : undefined
      }
    >
      {props.above && <div class="absolute inset-x-0 bottom-full">{props.above}</div>}
      <section
        ref={section}
        class={`sheet relative max-h-[70dvh] ${props.collapsible && collapsed() ? "overflow-hidden" : "overflow-y-auto"}`}
        style={{ "padding-bottom": "max(1.5rem, env(safe-area-inset-bottom))" }}
      >
        {props.collapsible && props.handle === "overlay" && (
          <button
            type="button"
            class="absolute inset-x-0 top-0 z-10 h-[30px]"
            aria-label={collapsed() ? "Agrandir le panneau" : "Réduire le panneau"}
            aria-expanded={!collapsed()}
            onClick={() => setCollapsed(!collapsed())}
          />
        )}
        {props.handle !== false &&
          props.handle !== "overlay" &&
          (props.collapsible ? (
            // 30 px grab zone over the whole width; the 40×4 handle stays where Figma puts it.
            <button
              type="button"
              class="-mx-5 -mt-[10px] flex h-[30px] w-[calc(100%+40px)] justify-center pt-[10px]"
              aria-label={collapsed() ? "Agrandir le panneau" : "Réduire le panneau"}
              aria-expanded={!collapsed()}
              onClick={() => setCollapsed(!collapsed())}
            >
              <span class="h-1 w-10 rounded-[2px] bg-handle" />
            </button>
          ) : (
            <div class="mx-auto mb-4 h-1 w-10 rounded-[2px] bg-handle" />
          ))}
        {props.onClose && (
          <button
            type="button"
            class="absolute right-[6px] top-[18px] h-11 w-11 flex items-center justify-center"
            onClick={props.onClose}
            aria-label="Fermer"
          >
            <img src="/icons/close.svg" width="24" height="24" alt="" />
          </button>
        )}
        {props.children}
      </section>
    </div>
  );
}
